import React, { useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./penSubMenu.module.css";
import consts from "../shared/consts";
import { Floater } from "./ui-components/floaters/floater";
import { PortalID } from "frontend/modal/portal-types";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal, arrow } from "@floating-ui/react";
import classNames from "classnames";
import { darkenBy } from "utils/color-utils";

export default function PenSubMenu({
  onMenuItemClick,
  relativeTo,
  onHover,
  onLeave,
}: {
  onMenuItemClick: (itemName: any) => void;
  relativeTo: React.RefObject<HTMLElement>;
  onHover?: () => void;
  onLeave?: () => void;
}) {
  const portal = document.getElementById(PortalID.Floaters);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const colors = isCimReorderEnabled ? consts.CIM_COLOR_PALETTE : consts.COLOR_PALETTE;
  const arrowRef = useRef(null);

  // Set up floating UI
  const { refs, floatingStyles } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the reference element when it changes
  React.useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [refs, relativeTo]);
  const ColorOption = ({ color, index, itemType }: { color: string; index: number; itemType: string }) => {
    return (
      <div
        key={`${color}_${index}`}
        className={styles.colorOption}
        style={{ backgroundColor: color, border: isCimReorderEnabled ? `1px solid ${darkenBy(color, 0.1)}` : "none" }}
        onClick={() => {
          onMenuItemClick && onMenuItemClick({ item: itemType, data: color });
        }}
      />
    );
  };

  const ColorPalette = ({ itemType }: { itemType: string }) => {
    return (
      <div className={classNames(styles.colors, isCimReorderEnabled && styles.colorsCIMFooterEnabled)}>
        {colors.map((i: any, index: number) => {
          return <ColorOption key={`${i}_${index}`} color={i} index={index} itemType={itemType} />;
        })}
      </div>
    );
  };

  // When CIM footer is enabled, use FloatingPortal
  if (isCimReorderEnabled && relativeTo.current) {
    return (
      <FloatingPortal id={PortalID.Floaters}>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={classNames(styles.container, styles.containerCIMFooterEnabled)}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          <ColorPalette itemType={consts.MENU_ITEMS.PEN} />
        </div>
      </FloatingPortal>
    );
  }

  // Original logic for non-CIM footer
  const element = relativeTo.current ? (
    <>
      <Floater relativeTo={relativeTo} side="right">
        <div className={styles.container} onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
          <ColorPalette itemType={consts.MENU_ITEMS.PEN} />
        </div>
      </Floater>
      <Floater
        relativeTo={relativeTo}
        side={"right"}
        extraStyle={{
          transition: "opacity 0.1s ease-out, transform 0.1s ease-out",
          marginLeft: "-6px",
        }}
      >
        <div className={styles.arrow} />
      </Floater>
    </>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}
